export function compareHolding(currentHolding, baseHolding, config) {
  const currentValue = Number(currentHolding[config.metric])

  if (!baseHolding) {
    return {
      id: config.id,
      daysAgo: config.daysAgo,
      metric: config.metric,
      baseValue: null,
      currentValue,
      delta: null,
      status: 'newHolding',
    }
  }

  const baseValue = Number(baseHolding[config.metric])
  const delta = currentValue - baseValue

  return {
    id: config.id,
    daysAgo: config.daysAgo,
    metric: config.metric,
    baseValue,
    currentValue,
    delta,
    status: delta > 0 ? 'increase' : delta < 0 ? 'decrease' : 'unchanged',
  }
}

function normalizeTrendDays(value, fallback = 30) {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return fallback
  }
  return Math.max(1, Math.round(number))
}

export function buildTrend(snapshotWindow, code, metric) {
  const trendMetric = metric === 'shares' ? 'shares' : 'weight'
  return {
    metric: trendMetric,
    points: snapshotWindow.map((snapshot) => {
      const holding = snapshot.holdings.find((item) => item.code === code)
      return {
        date: snapshot.date,
        value: Number(holding?.[trendMetric] ?? 0),
      }
    }),
  }
}

export function buildHoldingRows(snapshots, preferences) {
  if (!Array.isArray(snapshots) || snapshots.length === 0) {
    return []
  }

  const sortedSnapshots = [...snapshots].sort((a, b) => a.date.localeCompare(b.date))
  const latestSnapshot = sortedSnapshots.at(-1)
  const trendDays = normalizeTrendDays(preferences?.trendDays)
  const trendSnapshots = sortedSnapshots.slice(-trendDays)
  const trendMetric = preferences?.trendMetric === 'shares' ? 'shares' : 'weight'
  const selectedCodes = new Set(preferences?.selectedStockCodes ?? [])
  const holdingsByCode = new Map()

  sortedSnapshots.forEach((snapshot) => {
    snapshot.holdings.forEach((holding) => {
      holdingsByCode.set(holding.code, holding)
    })
  })

  const latestHoldingsByCode = new Map(latestSnapshot.holdings.map((holding) => [holding.code, holding]))
  const displayCodes = [
    ...latestSnapshot.holdings.map((holding) => holding.code),
    ...Array.from(holdingsByCode.keys()).filter((code) => !latestHoldingsByCode.has(code)),
  ]

  return displayCodes
    .filter((code) => selectedCodes.size === 0 || selectedCodes.has(code))
    .map((code) => {
      const latestHolding = latestHoldingsByCode.get(code)
      const isMissingLatestHolding = !latestHolding
      const holding = latestHolding ?? {
        ...holdingsByCode.get(code),
        weight: 0,
        shares: 0,
      }

      return {
        ...holding,
        trend: buildTrend(trendSnapshots, holding.code, trendMetric),
        comparisons: (preferences?.comparisonConfigs ?? []).map((config) => {
          const baseIndex = sortedSnapshots.length - 1 - config.daysAgo
          const baseSnapshot = sortedSnapshots[baseIndex]
          if (!baseSnapshot) {
            return {
              id: config.id,
              daysAgo: config.daysAgo,
              metric: config.metric,
              baseValue: null,
              currentValue: Number(holding[config.metric]),
              delta: null,
              status: 'missingBase',
            }
          }

          const baseHolding = baseSnapshot.holdings.find((item) => item.code === holding.code)
          if (!baseHolding && isMissingLatestHolding) {
            return {
              id: config.id,
              daysAgo: config.daysAgo,
              metric: config.metric,
              baseValue: null,
              currentValue: Number(holding[config.metric]),
              delta: null,
              status: 'missingBase',
            }
          }

          return compareHolding(holding, baseHolding, config)
        }),
      }
    })
}
